import { Link } from "react-router";

import { ArticleCard } from "@/components/pieces/learn/ArticleCard";
import { Button } from "@/components/ui/button";
import { ArticleReadStatusReadStatus } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

interface SeriesCardProps {
  series: {
    id: string;
    title: string;
    urlSlug: string;
    description?: string;
    articleCount?: number | null;
    previewArticles?: Array<{
      id: string;
      title?: string | null;
      urlSlug?: string | null;
      timeToRead?: number | null;
      heroImage?: string | null;
      summary?: string | null;
      updatedAt?: string | null;
      authorName?: string | null;
    } | null> | null;
  };
  showPreviewArticles?: boolean;
  readStatus?: {
    completed: string[];
    inProgress: string[];
    total: number | null | undefined;
  };
}

export function SeriesCard({
  series,
  showPreviewArticles,
  readStatus,
}: SeriesCardProps) {
  const previewArticles = ensureNonNullArray(series.previewArticles);
  const hasArticles = showPreviewArticles && previewArticles.length > 0;

  return (
    <div className="mb-8">
      {/* Series Header */}
      <div className="flex flex-col md:flex-row gap-3 justify-between md:gap-20 md:items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold m-0">
            <Link
              to={`/learn/series/${series.urlSlug}`}
              className="text-gray-700 hover:underline"
            >
              {series.title}
            </Link>
          </h3>
          {series.description && (
            <p className="mt-2 text-gray-600">{series.description}</p>
          )}
        </div>

        <Button intent="secondary" variant="outline" size="sm" asChild>
          <Link
            to={`/learn/series/${series.urlSlug}`}
            className="whitespace-nowrap"
          >
            View All {series.articleCount ? `(${series.articleCount})` : ""} →
          </Link>
        </Button>
      </div>

      {/* Preview Articles Grid */}
      {hasArticles && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {previewArticles.map((article) => {
            const isCompleted = readStatus?.completed.includes(article.id);
            const isInProgress = readStatus?.inProgress.includes(article.id);

            return (
              <ArticleCard
                key={article.id}
                item={{
                  ...article,
                  currentUserReadStatus: isCompleted
                    ? ArticleReadStatusReadStatus.ReadingCompleted
                    : isInProgress
                      ? ArticleReadStatusReadStatus.ReadingInProgress
                      : undefined,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
